import { AGENT_REQUIRED_ROLES } from '@doneverse/shared'
import { Outlet } from '@tanstack/react-router'
import { Activity, CreditCard, MonitorDown, User } from 'lucide-react'
import type { SidebarNavItem } from './SectionSidebar'
import { SectionSidebar } from './SectionSidebar'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useAuth } from '@/contexts/AuthContext'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'

// Billing lives in Keap/Infusionsoft, not in the dashboard — the tab is an
// external hand-off to the same order form the Hub links "Update Payment
// Details" at (ClickUp 86d3n5zfn). Founders only: they are the ones billed.
const UPDATE_BILLING_URL = 'https://zj721.infusionsoft.app/app/orderForms/Update-Credit-Card'

const profileNavItem: SidebarNavItem = { to: '/settings', label: 'Your Profile', icon: User, end: true }
const updateBillingNavItem: SidebarNavItem = {
  href: UPDATE_BILLING_URL,
  label: 'Update Billing',
  icon: CreditCard,
}
// AGENT_REQUIRED_ROLES only (doers, and DSLs, who install DoneFlow on the
// account they view their team with). The API agrees: /v1/agent/devices/register
// and /v1/activity-events are requireRole(...AGENT_REQUIRED_ROLES). Admins reach
// the page by URL rather than the sidebar, which is how they check a published
// release; the download endpoint admits them too
// (requireRole(...AGENT_REQUIRED_ROLES, 'admin') on GET
// /v1/me/agent/latest-release). The layout's other four roles — founder,
// doing_team, intake_team, escalation_officer — can still render the route,
// but the endpoint answers them 403, so they would only see the error state.
//
// This entry is also how a doer finds the page without the tray link.
const activityAgentNavItem: SidebarNavItem = {
  to: '/settings/activity-agent',
  label: 'Activity Agent',
  icon: MonitorDown,
}

// The caller's own recorded activity (ClickUp 86d40rj6v). AGENT_REQUIRED_ROLES
// only (doers and DSLs, ClickUp 14zf25zn6na), and only with the my-activity
// flag; the API refuses everyone else.
const myActivityNavItem: SidebarNavItem = {
  to: '/settings/my-activity',
  label: 'My Activity',
  icon: Activity,
}

export function SettingsLayout() {
  const { profile } = useAuth()
  const { hasFlag } = useFeatureFlags()
  const runsAgent = (AGENT_REQUIRED_ROLES as readonly string[]).includes(profile?.role ?? '')
  const navItems = [
    profileNavItem,
    ...(profile?.role === 'founder' ? [updateBillingNavItem] : []),
    ...(runsAgent ? [activityAgentNavItem] : []),
    ...(runsAgent && hasFlag('my-activity') ? [myActivityNavItem] : []),
  ]

  return (
    <>
      <SectionSidebar navItems={navItems} title="Settings" />
      <main className="flex-1 min-h-0 min-w-0 flex flex-col bg-surface-muted">
        <ScrollArea scrollRestorationId="settings-scroll" className="flex-1 min-h-0">
          <Outlet />
        </ScrollArea>
      </main>
    </>
  )
}
